import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Input,
  InputNumber,
  message,
  Modal,
  Form,
  Select,
  Radio,
  Upload,
  Space,
  Typography,
  Divider,
  Switch,
  Row,
  Col,
} from 'antd';
import {
  CopyOutlined,
  PlayCircleOutlined,
  StopOutlined,
  UploadOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

// 直播设置表单数据接口
interface LiveStreamFormData {
  title: string;
  coverImages: string[];
  category: string;
  gameCategory: string;
  gameVenue: string;
  secondaryGame: string;
  quality: string;
  environment: 'production' | 'gray';
  channel: string;
  pkGame?: string;
  gameIcons: string[];
  // 收费相关
  isPaid: boolean;
  paymentType?: 'ticket' | 'time'; // 按场收费 | 按时收费
  price?: number; // 定价
  quickAmounts?: number[]; // 快捷金额（从后台配置读取）
  supportTrial?: boolean; // 是否支持免费试看
}

const AnchorLiveStream: React.FC = () => {
  const [form] = Form.useForm();
  const [isStreaming, setIsStreaming] = useState(false);
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [paymentType, setPaymentType] = useState<'ticket' | 'time'>('time');
  const [supportTrial, setSupportTrial] = useState(false);
  
  // 从后台配置读取的快捷金额（从LiveStreamSettings的配置读取）
  const [paymentConfig, setPaymentConfig] = useState({
    ticket: {
      quickAmounts: [100, 500, 1000, 5000],
      minAmount: 10,
      maxAmount: 10000,
      defaultAmount: 100,
    },
    time: {
      quickAmounts: [10, 20, 50, 100],
      minAmount: 10,
      maxAmount: 5000,
      defaultAmount: 10,
    },
  });

  // 从后台加载付费配置
  useEffect(() => {
    const loadPaymentConfig = async () => {
      try {
        // 模拟API调用，实际应从后台获取
        // const response = await fetch('/api/live-stream/payment-config');
        // const data = await response.json();
        
        // 暂时使用模拟数据，实际应该从后台配置读取
        const mockConfig = {
          ticket: {
            quickAmounts: [100, 500, 1000, 5000],
            minAmount: 10,
            maxAmount: 10000,
            defaultAmount: 100,
          },
          time: {
            quickAmounts: [10, 20, 50, 100],
            minAmount: 10,
            maxAmount: 5000,
            defaultAmount: 10,
          },
        };
        setPaymentConfig(mockConfig);
      } catch (error) {
        console.error('加载付费配置失败:', error);
      }
    };
    loadPaymentConfig();
  }, []);

  // 模拟的推流信息
  const [streamInfo, setStreamInfo] = useState({
    serverUrl: 'rtmp://207489.push.tlivecloud.com/live/',
    streamKey: '20015302_256739_main?txSecret=6ddd197b5f921d40e741435878e7272a&txTime=69AEEE80',
    expireTime: Date.now() + 600 * 60 * 1000, // 600分钟后过期
  });

  // 检查推流信息是否过期
  useEffect(() => {
    const checkExpiry = () => {
      if (Date.now() > streamInfo.expireTime) {
        message.warning('推流信息已过期，请刷新页面重新获取');
      }
    };
    const interval = setInterval(checkExpiry, 60000); // 每分钟检查一次
    return () => clearInterval(interval);
  }, [streamInfo.expireTime]);

  // 复制服务器地址
  const handleCopyServerUrl = () => {
    navigator.clipboard.writeText(streamInfo.serverUrl);
    message.success('服务器地址已复制到剪贴板');
  };

  // 复制串流密码
  const handleCopyStreamKey = () => {
    navigator.clipboard.writeText(streamInfo.streamKey);
    message.success('串流密码已复制到剪贴板');
  };

  // 打开设置弹窗
  const handleOpenSettings = () => {
    setSettingsModalVisible(true);
    // 重置表单
    form.resetFields();
    form.setFieldsValue({
      isPaid: false,
      supportTrial: false,
      environment: 'production',
    });
    setIsPaid(false);
    setSupportTrial(false);
  };

  // 开始直播
  const handleStartLive = async (values: LiveStreamFormData) => {
    try {
      // 验证推流信息是否过期
      if (Date.now() > streamInfo.expireTime) {
        message.error('推流信息已过期，请刷新页面重新获取');
        return;
      }

      // 模拟API调用
      await new Promise((resolve) => setTimeout(resolve, 1000));
      
      console.log('开始直播，配置信息：', values);
      setIsStreaming(true);
      setSettingsModalVisible(false);
      message.success('直播已开始');
    } catch (error) {
      message.error('开播失败，请重试');
    }
  };

  // 结束直播
  const handleStopLive = async () => {
    try {
      // 模拟API调用
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setIsStreaming(false);
      message.success('直播已结束');
    } catch (error) {
      message.error('结束直播失败，请重试');
    }
  };

  // 刷新推流信息
  const handleRefreshStreamInfo = async () => {
    try {
      // 模拟API调用获取新的推流信息
      await new Promise((resolve) => setTimeout(resolve, 500));
      setStreamInfo({
        serverUrl: 'rtmp://207489.push.tlivecloud.com/live/',
        streamKey: `20015302_${Date.now()}_main?txSecret=${Math.random().toString(36).substring(7)}&txTime=${Date.now().toString(16).toUpperCase()}`,
        expireTime: Date.now() + 600 * 60 * 1000,
      });
      message.success('推流信息已刷新');
    } catch (error) {
      message.error('刷新失败，请重试');
    }
  };

  // 获取当前快捷金额
  const getCurrentQuickAmounts = () => {
    return isPaid ? paymentConfig[paymentType].quickAmounts : [];
  };

  // 选择快捷金额
  const handleSelectQuickAmount = (amount: number) => {
    form.setFieldsValue({ price: amount });
    // 强制更新表单显示
    form.validateFields(['price']);
  };

  return (
    <div style={{ padding: 24, background: '#fff', minHeight: '100vh' }}>
      <Card style={{ background: '#fff' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {/* 温馨提示 */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 16 }}>温馨提示</div>
            <div style={{ lineHeight: 2.2, fontSize: 14, color: '#333' }}>
              <div>1: 使用OBS直播软件进行直播</div>
              <div>2: 打开OBS将下面两处复制填写进行推流</div>
              <div>3: 请在600分钟内推流，超出时间请刷新页面重新获取推流信息</div>
              <div>3: 先使用OBS软件推流，推流成功后再点击本页面的开始直播按钮</div>
              <div>4: 关播时先关闭OBS推流，再点击本页面关播按钮</div>
              <div>5: OBS推流后点击开播按钮如果没有播放画面说明OBS推流失败，请重启OBS后重新推流。</div>
            </div>
          </div>

          {/* 服务器地址 */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Text strong style={{ minWidth: 100, fontSize: 14 }}>服务器地址:</Text>
              <Input
                value={streamInfo.serverUrl}
                readOnly
                style={{ flex: 1 }}
              />
              <Button
                type="primary"
                icon={<CopyOutlined />}
                onClick={handleCopyServerUrl}
              >
                点我复制
              </Button>
            </div>
          </div>

          {/* 串流密码 */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Text strong style={{ minWidth: 100, fontSize: 14 }}>串流密码:</Text>
              <Input
                value={streamInfo.streamKey}
                readOnly
                style={{ flex: 1 }}
              />
              <Button
                type="primary"
                icon={<CopyOutlined />}
                onClick={handleCopyStreamKey}
              >
                点我复制
              </Button>
            </div>
          </div>

          {/* 操作按钮 */}
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            {!isStreaming ? (
              <Button
                type="primary"
                size="large"
                icon={<PlayCircleOutlined />}
                onClick={handleOpenSettings}
                style={{
                  height: 48,
                  fontSize: 16,
                  paddingLeft: 32,
                  paddingRight: 32,
                  background: '#ff7a45',
                  borderColor: '#ff7a45',
                }}
              >
                开始直播
              </Button>
            ) : (
              <Button
                danger
                size="large"
                icon={<StopOutlined />}
                onClick={handleStopLive}
                style={{
                  height: 48,
                  fontSize: 16,
                  paddingLeft: 32,
                  paddingRight: 32,
                }}
              >
                结束直播
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* 直播设置弹窗 */}
      <Modal
        title="直播设置"
        open={settingsModalVisible}
        onCancel={() => setSettingsModalVisible(false)}
        footer={null}
        width={800}
        style={{ top: 20 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleStartLive}
          initialValues={{
            isPaid: false,
            supportTrial: false,
            environment: 'production',
            paymentType: 'time',
          }}
        >
          {/* 直播标题 */}
          <Form.Item
            label="*直播标题"
            name="title"
            rules={[{ required: true, message: '请填写直播标题' }]}
          >
            <Input placeholder="请填写直播标题" />
          </Form.Item>

          {/* 直播封面 */}
          <Form.Item
            label="*直播封面"
            name="coverImages"
            rules={[{ required: true, message: '请上传直播封面' }]}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Upload
                  listType="picture-card"
                  maxCount={1}
                  accept=".webp"
                  beforeUpload={(file) => {
                    // 检查文件格式
                    if (file.type !== 'image/webp') {
                      message.error('只支持webp格式');
                      return false;
                    }
                    // 检查文件大小（40KB）
                    if (file.size > 40 * 1024) {
                      message.error('图片大小不能超过40KB');
                      return false;
                    }
                    return false; // 阻止自动上传
                  }}
                >
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>请上传本地图片</div>
                  </div>
                </Upload>
              </Col>
              <Col span={12}>
                <Upload
                  listType="picture-card"
                  maxCount={1}
                  accept=".webp"
                  beforeUpload={(file) => {
                    if (file.type !== 'image/webp') {
                      message.error('只支持webp格式');
                      return false;
                    }
                    if (file.size > 40 * 1024) {
                      message.error('图片大小不能超过40KB');
                      return false;
                    }
                    return false;
                  }}
                >
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>请上传本地图片</div>
                  </div>
                </Upload>
              </Col>
            </Row>
            <div style={{ color: '#999', fontSize: 12, marginTop: 8 }}>
              只支持webp格式，大小不能超过40KB
            </div>
          </Form.Item>

          {/* 直播分类 */}
          <Form.Item
            label="*直播分类"
            name="category"
            rules={[{ required: true, message: '请选择直播分类' }]}
          >
            <Select placeholder="请选择直播分类">
              <Option value="entertainment">娱乐</Option>
              <Option value="game">游戏</Option>
              <Option value="sports">体育</Option>
            </Select>
          </Form.Item>

          {/* 游戏大类 */}
          <Form.Item
            label="*游戏大类"
            name="gameCategory"
            rules={[{ required: true, message: '请选择游戏大类' }]}
          >
            <Select placeholder="请选择游戏大类">
              <Option value="live">真人视讯</Option>
              <Option value="slot">电子游戏</Option>
              <Option value="sports">体育</Option>
            </Select>
          </Form.Item>

          {/* 游戏场馆 */}
          <Form.Item
            label="*游戏场馆"
            name="gameVenue"
            rules={[{ required: true, message: '请选择游戏场馆' }]}
          >
            <Select placeholder="请选择游戏场馆">
              <Option value="evo">EVO</Option>
              <Option value="ag">AG</Option>
              <Option value="dg">DG</Option>
            </Select>
          </Form.Item>

          {/* 二级游戏 */}
          <Form.Item
            label="*二级游戏"
            name="secondaryGame"
            rules={[{ required: true, message: '请选择二级游戏' }]}
          >
            <Select placeholder="请选择二级游戏">
              <Option value="baccarat">百家乐</Option>
              <Option value="roulette">轮盘</Option>
              <Option value="dragon-tiger">龙虎</Option>
            </Select>
          </Form.Item>

          {/* 直播画质 */}
          <Form.Item
            label="*直播画质"
            name="quality"
            rules={[{ required: true, message: '请选择直播画质' }]}
          >
            <Select placeholder="请选择直播画质">
              <Option value="720">720P</Option>
              <Option value="1080">1080P</Option>
              <Option value="4k">4K</Option>
            </Select>
          </Form.Item>

          {/* 开播环境 */}
          <Form.Item
            label="*开播环境"
            name="environment"
            rules={[{ required: true, message: '请选择开播环境' }]}
          >
            <Radio.Group>
              <Radio value="production">生产</Radio>
              <Radio value="gray">灰度</Radio>
            </Radio.Group>
          </Form.Item>

          {/* 渠道 */}
          <Form.Item
            label="*渠道"
            name="channel"
            rules={[{ required: true, message: '请选择渠道' }]}
          >
            <Select placeholder="请选择渠道">
              <Option value="aa">AA</Option>
              <Option value="bb">BB</Option>
            </Select>
          </Form.Item>

          {/* PK游戏 */}
          <Form.Item
            label="PK游戏"
            name="pkGame"
          >
            <Select placeholder="请选择PK游戏" allowClear>
              <Option value="baccarat">百家乐</Option>
              <Option value="roulette">轮盘</Option>
            </Select>
          </Form.Item>

          {/* 游戏图标 */}
          <Form.Item
            label="游戏图标"
            name="gameIcons"
          >
            <Row gutter={16}>
              <Col span={12}>
                <Upload
                  listType="picture-card"
                  maxCount={1}
                  accept=".webp"
                  beforeUpload={(file) => {
                    if (file.type !== 'image/webp') {
                      message.error('只支持webp格式');
                      return false;
                    }
                    if (file.size > 40 * 1024) {
                      message.error('图片大小不能超过40KB');
                      return false;
                    }
                    return false;
                  }}
                >
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>请上传本地图片</div>
                  </div>
                </Upload>
              </Col>
              <Col span={12}>
                <Upload
                  listType="picture-card"
                  maxCount={1}
                  accept=".webp"
                  beforeUpload={(file) => {
                    if (file.type !== 'image/webp') {
                      message.error('只支持webp格式');
                      return false;
                    }
                    if (file.size > 40 * 1024) {
                      message.error('图片大小不能超过40KB');
                      return false;
                    }
                    return false;
                  }}
                >
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>请上传本地图片</div>
                  </div>
                </Upload>
              </Col>
            </Row>
            <div style={{ color: '#999', fontSize: 12, marginTop: 8 }}>
              只支持webp格式，大小不能超过40KB
            </div>
          </Form.Item>

          <Divider />

          {/* 是否收费 */}
          <Form.Item
            label="是否收费"
            name="isPaid"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="是"
              unCheckedChildren="否"
              onChange={(checked) => {
                setIsPaid(checked);
                if (!checked) {
                  form.setFieldsValue({
                    supportTrial: false,
                    paymentType: undefined,
                    price: undefined,
                  });
                  setSupportTrial(false);
                } else {
                  // 设置默认值
                  form.setFieldsValue({
                    paymentType: 'time',
                    price: paymentConfig.time.defaultAmount,
                  });
                  setPaymentType('time');
                }
              }}
            />
          </Form.Item>

          {/* 收费相关配置 */}
          {isPaid && (
            <>
              {/* 是否支持免费试看 */}
              <Form.Item
                label="是否支持免费试看"
                name="supportTrial"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="是"
                  unCheckedChildren="否"
                  onChange={setSupportTrial}
                />
              </Form.Item>

              {/* 付费方式 */}
              <Form.Item
                label="付费方式"
                name="paymentType"
                rules={[{ required: true, message: '请选择付费方式' }]}
              >
                <Select<'ticket' | 'time'>
                  placeholder="请选择付费方式"
                  onChange={(value) => {
                    setPaymentType(value);
                    // 切换付费方式时，更新默认金额
                    form.setFieldsValue({
                      price: paymentConfig[value].defaultAmount,
                    });
                  }}
                >
                  <Option value="ticket">按场收费</Option>
                  <Option value="time">按时收费</Option>
                </Select>
              </Form.Item>

              {/* 定价 */}
              <Form.Item
                label={paymentType === 'time' ? '定价(每分钟)' : '定价(每场)'}
                name="price"
                rules={[
                  { required: true, message: '请输入定价' },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const config = paymentConfig[paymentType];
                      if (value < config.minAmount) {
                        return Promise.reject(`定价不能低于${config.minAmount}`);
                      }
                      if (value > config.maxAmount) {
                        return Promise.reject(`定价不能超过${config.maxAmount}`);
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  min={paymentConfig[paymentType].minAmount}
                  max={paymentConfig[paymentType].maxAmount}
                  style={{ width: '100%' }}
                  placeholder={`请输入定价（${paymentConfig[paymentType].minAmount}-${paymentConfig[paymentType].maxAmount}）`}
                  addonAfter={paymentType === 'time' ? '每分钟' : '每场'}
                />
              </Form.Item>

              {/* 快捷金额 */}
              {getCurrentQuickAmounts().length > 0 && (
                <Form.Item label="快捷选择金额">
                  <Space wrap>
                    {getCurrentQuickAmounts().map((amount) => {
                      const currentPrice = form.getFieldValue('price');
                      return (
                        <Button
                          key={amount}
                          type={currentPrice === amount ? 'primary' : 'default'}
                          onClick={() => handleSelectQuickAmount(amount)}
                          style={{
                            minWidth: 80,
                            height: 36,
                          }}
                        >
                          {amount}
                        </Button>
                      );
                    })}
                  </Space>
                  <div style={{ color: '#999', fontSize: 12, marginTop: 8 }}>
                    点击快捷金额按钮可快速设置定价
                  </div>
                </Form.Item>
              )}
            </>
          )}

          {/* 提交按钮 */}
          <div style={{ textAlign: 'right', marginTop: 24 }}>
            <Space>
              <Button onClick={() => setSettingsModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                确定
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AnchorLiveStream;

